import { useEffect, useMemo, useRef, useState } from 'react'
import type { VersusDisplayData, VersusFighter } from './versusBoardUtils'
import { mainEventPercents } from './versusBoardUtils'
import { normalizePredictionDisplayConfig, type PredictionDisplayConfig } from './predictionDisplayBackgroundUtils'

function useAnimatedNumber(target: number, duration = 900) {
  const [display, setDisplay] = useState(target)
  const prevRef = useRef(target)
  useEffect(() => {
    const start = prevRef.current
    const diff = target - start
    if (diff === 0) return
    const t0 = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration)
      const eased = 1 - (1 - p) ** 3
      setDisplay(Math.round((start + diff * eased) * 10) / 10)
      if (p < 1) raf = requestAnimationFrame(tick)
      else prevRef.current = target
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])
  return display
}

function FighterPortrait({ fighter, side, large }: { fighter: VersusFighter; side: 'left' | 'right'; large?: boolean }) {
  const glowColor = side === 'left' ? 'rgba(220,38,38,0.35)' : 'rgba(37,99,235,0.35)'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: large ? 220 : undefined }}>
      <div style={{ position: 'relative', height: large ? 200 : 64, width: large ? 160 : 56 }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: large ? 16 : 8,
            background: `radial-gradient(circle at 50% 80%, ${glowColor}, transparent 70%)`,
            filter: 'blur(12px)',
          }}
        />
        {fighter.imageUrl ? (
          <img
            src={fighter.imageUrl}
            alt=""
            style={{
              position: 'relative',
              height: '100%',
              width: '100%',
              objectFit: 'cover',
              objectPosition: 'top',
              borderRadius: large ? 16 : 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
              border: '2px solid rgba(255,255,255,0.15)',
            }}
          />
        ) : (
          <div
            style={{
              position: 'relative',
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: large ? 16 : 8,
              background: '#1e293b',
              color: 'rgba(255,255,255,0.7)',
              fontWeight: 900,
              fontSize: large ? 32 : 11,
            }}
          >
            {fighter.label.slice(0, 2).toUpperCase()}
          </div>
        )}
      </div>
      <div
        style={{
          position: 'relative',
          marginTop: -10,
          width: '100%',
          padding: large ? '8px 12px' : '4px 8px',
          textAlign: 'center',
          color: '#fff',
          backgroundColor: fighter.barColor,
          clipPath: 'polygon(8% 0, 92% 0, 100% 100%, 0 100%)',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: large ? 14 : 10, textTransform: 'uppercase' }}>{fighter.label}</div>
        {fighter.subtitle ? (
          <div style={{ fontSize: large ? 11 : 9, opacity: 0.9 }}>{fighter.subtitle}</div>
        ) : null}
      </div>
    </div>
  )
}

type Props = {
  title: string
  subtitle?: string
  footerTitle?: string
  footerNote?: string
  data: VersusDisplayData
  updatedAt?: string
  live?: boolean
  loading?: boolean
  emptyMessage?: string
  displayConfig?: PredictionDisplayConfig | null
}

export default function VsMatchupLiveBoard({
  title,
  subtitle,
  footerTitle,
  footerNote,
  data,
  updatedAt,
  live = true,
  loading = false,
  emptyMessage = 'ยังไม่มีข้อมูลการวางเดิมพัน',
  displayConfig,
}: Props) {
  const bgConfig = normalizePredictionDisplayConfig(displayConfig)
  const hasBackground = Boolean(bgConfig.backgroundImageUrl)
  const percents = useMemo(
    () => (data.mainEvent ? mainEventPercents(data.mainEvent) : { left: 50, right: 50 }),
    [data.mainEvent],
  )
  const leftPct = useAnimatedNumber(percents.left)
  const rightPct = useAnimatedNumber(percents.right)

  return (
    <div
      className="vs-matchup-live-board mb-3"
      style={{
        position: 'relative',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
      }}
    >
      {hasBackground ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${bgConfig.backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 15% 20%, rgba(220,38,38,0.18), transparent 42%), radial-gradient(circle at 85% 20%, rgba(37,99,235,0.18), transparent 42%), linear-gradient(180deg, #0f172a 0%, #020617 55%, #000 100%)',
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: '#000',
          opacity: hasBackground ? (bgConfig.backgroundOverlayOpacity ?? 0.6) : 0.35,
        }}
      />

      <div style={{ position: 'relative', zIndex: 1, padding: '16px 18px' }}>
        <div style={{ marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#fbbf24', textTransform: 'uppercase' }}>
            Main Event · 1 vs 1
          </div>
          <h3 style={{ margin: '6px 0 4px', fontSize: 22, fontWeight: 900, color: '#fff', textTransform: 'uppercase' }}>
            ทายผลผู้ชนะ
          </h3>
          <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>{subtitle || title}</p>
          {live ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 8,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(220,38,38,0.9)',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />
              Live
            </span>
          ) : null}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>กำลังโหลด...</div>
        ) : !data.mainEvent ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>{emptyMessage}</div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'end',
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <FighterPortrait fighter={data.mainEvent.left} side="left" large />
              </div>
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div
                  style={{
                    fontSize: 42,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    background: 'linear-gradient(180deg, #fde68a, #f59e0b, #b45309)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  VS
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <FighterPortrait fighter={data.mainEvent.right} side="right" large />
              </div>
            </div>

            <div style={{ position: 'relative', height: 56, marginBottom: 8, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: `${leftPct}%`,
                  background: 'linear-gradient(90deg, #b91c1c, #ef4444)',
                  transition: 'width 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
                  clipPath: 'polygon(0 0, 100% 0, calc(100% - 14px) 100%, 0 100%)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  width: `${rightPct}%`,
                  background: 'linear-gradient(270deg, #1d4ed8, #3b82f6)',
                  transition: 'width 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
                  clipPath: 'polygon(14px 0, 100% 0, 100% 100%, 0 100%)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 16px',
                  color: '#fff',
                  fontSize: 20,
                  fontWeight: 900,
                }}
              >
                <span>{leftPct.toFixed(1)}%</span>
                <span>{rightPct.toFixed(1)}%</span>
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  bottom: 0,
                  width: 2,
                  transform: 'translateX(-50%)',
                  background: 'rgba(255,255,255,0.85)',
                  boxShadow: '0 0 16px #fff',
                }}
              />
            </div>

            <div style={{ marginBottom: 16, textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>
              ยอดรวม {data.totalStake.toLocaleString('th-TH')} บาท · {data.totalBets.toLocaleString('th-TH')} ใบ
              {updatedAt ? ` · อัปเดต ${new Date(updatedAt).toLocaleString('th-TH')}` : ''}
            </div>

            {data.undercard.length > 0 ? (
              <div style={{ borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.25)', padding: 12 }}>
                <div style={{ marginBottom: 10, textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', color: '#94a3b8', textTransform: 'uppercase' }}>
                  Undercard · คู่แข่งอื่น
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.undercard.map((match) => (
                    <div
                      key={match.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 12,
                        background: 'rgba(255,255,255,0.05)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {match.left.imageUrl ? (
                          <img src={match.left.imageUrl} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(127,29,29,0.5)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {match.left.label.slice(0, 2)}
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{match.left.label}</div>
                          <div style={{ fontSize: 10, color: '#fca5a5' }}>{match.left.stakePercent.toFixed(1)}%</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 10, fontWeight: 900, fontStyle: 'italic', color: '#fbbf24' }}>VS</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, minWidth: 0 }}>
                        <div style={{ minWidth: 0, textAlign: 'right' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{match.right.label}</div>
                          <div style={{ fontSize: 10, color: '#93c5fd' }}>{match.right.stakePercent.toFixed(1)}%</div>
                        </div>
                        {match.right.imageUrl ? (
                          <img src={match.right.imageUrl} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(30,58,138,0.5)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {match.right.label.slice(0, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}

        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, color: '#fff' }}>{footerTitle || title}</div>
          {footerNote ? <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>{footerNote}</div> : null}
        </div>
      </div>
    </div>
  )
}
